/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from "react";

export interface FlaggedMonster {
  name: string;
  open: number;      // outstanding unadjudicated violation/copyright reports
  confirmed: number; // moderator-confirmed violations / rejections
  errors: number;    // outstanding data-error reports (feedback; don't hide the monster)
}

interface MyFlaggedMonstersModalProps {
  isOpen: boolean;
  onClose: () => void;
  monsters: FlaggedMonster[];
  // Open the given monster in the editor (where the owner can View reports / Resolve / revise).
  onSelectMonster: (name: string) => void;
}

// The owner's personal "moderation queue": a list of their OWN monsters that currently have
// outstanding violation/copyright reports. Selecting one opens it in the editor, where the existing
// "View reports" control lets the owner read the reasons and resolve each report.
const MyFlaggedMonstersModal: React.FC<MyFlaggedMonstersModalProps> = ({ isOpen, onClose, monsters, onSelectMonster }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ width: '520px', height: 'auto', minHeight: '220px' }} onClick={(e) => e.stopPropagation()}>
        <span className="modal-title" style={{ display: 'block', textAlign: 'center' }}>Your Monsters With Reports</span>
        <div>
          {monsters.length === 0 && <p>None of your monsters have outstanding reports.</p>}
          {monsters.length > 0 && (
            <>
              <p style={{ fontSize: 'small' }}>
                These monsters have reports filed against them. Violations hide the monster until
                resolved; data-error reports are feedback. Open one to read and resolve the report(s).
              </p>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                {monsters.map((m) => {
                  const violations = m.open + m.confirmed;
                  const parts: string[] = [];
                  if (violations > 0) {
                    parts.push(`${violations} violation${violations === 1 ? '' : 's'}${m.confirmed > 0 ? ` (${m.confirmed} confirmed)` : ''}`);
                  }
                  if (m.errors > 0) {
                    parts.push(`${m.errors} data issue${m.errors === 1 ? '' : 's'}`);
                  }
                  return (
                    <li key={m.name} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button type="button" onClick={() => onSelectMonster(m.name)}>Open</button>
                      <span style={{ flex: 1 }}>
                        <b>{m.name}</b>
                        <span style={{ fontSize: 'small', color: '#c00', marginLeft: '8px' }}>{parts.join(', ')}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <div className="modal-actions" style={{ textAlign: 'center' }}>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default MyFlaggedMonstersModal;
